import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [gym, setGym] = useState(null);
  const [trialExpired, setTrialExpired] = useState(false);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Only fetch gym if user has a gym_id (not superadmin)
      if (response.data.gym_id) {
        const gymResponse = await axios.get(`${API}/gym`);
        setGym(gymResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
      setGym(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        await fetchUser();
      } else {
        setLoading(false);
      }
    };
    initAuth();
  }, [token]);

  // Axios interceptor to handle trial expiration (402 responses)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 402) {
          setTrialExpired(true);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const fetchGym = async () => {
    try {
      const response = await axios.get(`${API}/gym`);
      setGym(response.data);
    } catch (error) {
      console.error('Failed to fetch gym:', error);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    // Only fetch gym if user has a gym_id and is not a fighter
    if (userData.gym_id && userData.role !== 'fighter') {
      await fetchGym();
    }
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    await fetchGym();
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setGym(null);
  };

  const refreshGym = async () => {
    await fetchGym();
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = user?.role === 'superadmin';
  const isFighter = user?.role === 'fighter';

  const clearTrialExpired = () => setTrialExpired(false);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      gym, 
      loading, 
      login, 
      register, 
      logout, 
      isAdmin,
      isSuperAdmin,
      isFighter,
      refreshGym,
      refreshUser,
      trialExpired,
      clearTrialExpired
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
